import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { mapToBase } from "go-core/api/searchSelectService";
import { MoneyApi } from "go-core/api/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import { getReportCustomFieldsResources, reportParamsConverter } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import SynchronizeTransactionsReportModal from "../../components/SynchronizeTransactionsReportModal";

interface State {
	resource?: Record<string, any>;
	customFields: CustomFieldTemplateApi[];
	showSynchronizeReportModal: boolean;
	setShowSynchronizeReportModal: (value: boolean) => void;
}

const listName = "ADMIN_TRANSACTION_REPORT";
const resourceType = "TRANSACTION_REPORT";

const transactionStatuses = ["PAID", "PAYMENT_DUE", "VOIDED", "NOT_PAID"];
const transactionTypes = ["DOWNGRADE", "UPGRADE", "CHURN", "NEW", "REACTIVATION", "DOWNGRADE_PLAN", "UPGRADE_PLAN"];
const productNames = [
	"GOPOS_PLAN",
	"GOPOS_ARCHIVE",
	"GOPOS_ADDONS",
	"GOPOS_KDS",
	"GOPOS_KIOSK",
	"GOPOS_TERMINAL",
	"GOPOS_MOBILE_TERMINAL",
	"GOPOS_NUMBER_DISPLAY",
	"GOSTOCK_PLAN",
	"GOSTOCK_IMPORT",
	"GOSTOCK_SUBSTOCK",
	"GOSTOCK_TRANSFER",
	"GOSTOCK_INVENTORY_APP",
	"ADDON_HOTEL",
	"ADDON_PHONE_CENTRAL",
	"MULTILOCATIONS",
	"DRIVERS",
	"EMENU",
	"GOORDER",
	"GOCRM",
	"GOHUB",
	"GOPOS_RENT_TERMINAL",
];

const Report = ({ resource, customFields, setShowSynchronizeReportModal, showSynchronizeReportModal }: State) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onRefresh = async () => {
		if (config.fetch) {
			await config.fetch({});
		}
	};
	const getCFOptions = (slug: string) => {
		const cfOptions = customFields.filter((x) => x.slug === slug)[0];
		if (cfOptions === undefined) return undefined;

		const record: Record<string, string> = cfOptions.options.reduce((acc: any, obj: any) => {
			acc[obj.value] = obj.value;
			return acc;
		}, {});
		return record;
	};

	const parseFiltersCF = (options: any, customId?: string) => {
		let id = options.id;
		if (customId !== undefined) {
			id = customId;
		}
		const cfOptions = getCFOptions(id);
		if (cfOptions === undefined) return { ...options, type: "text" };
		return { ...options, type: "list", listOptions: cfOptions };
	};

	let config = {
		reportConfigId: "admin_transactions_report",
		aggregatePrefix: "summary",
		selectedColumns: [
			"start_quantity_customer",
			"new_quantity_customer",
			"reactivation_quantity_customer",
			"churn_quantity_customer",
			"quantity_customer",
			"start_total_price",
			"upgrade_total_price",
			"downgrade_total_price",
			"new_total_price",
			"reactivation_total_price",
			"churn_total_price",
			"total_price",
		],
		groups: [
			{
				id: "COMPANY_SOURCE",
				name: "Źródło",
			},
			{
				id: "CLIENT_TAX_ID_NO",
				name: t("lib:common.word.tax_id_no.title"),
			},
			{
				id: "NAME",
				name: t("lib:common.word.name"),
			},
			{
				id: "ORGANIZATION_NAME",
				name: t("lib:common.word.location"),
			},
			{
				id: "ORGANIZATION_PARTNER_CS",
				name: "Partner CS",
			},
			{
				id: "ORGANIZATION_PARTNER_RESELLER",
				name: "Partner Reseller",
			},
			{
				id: "ORGANIZATION_PARTNER_KAM",
				name: "Partner KAM",
			},
			{
				id: "ORGANIZATION_PARTNER_SALE",
				name: "Partner Sales",
			},
			{
				id: "PAID_AT",
				name: t("modules.transaction.field.paid_at.title"),
			},
			{
				id: "PAID_AT_MONTH",
				name: "Data płatność - miesiąc",
			},
			{
				id: "PAID_AT_YEAR",
				name: "Data płatność - rok",
			},
			{
				id: "TRANSACTION_AT_MONTH",
				name: "Data transakcji - miesiąc",
			},
			{
				id: "TRANSACTION_AT",
				name: t("modules.transaction_report.field.transaction_at.title"),
			},
			{
				id: "CLIENT_NAME",
				name: t("modules.transaction_report.field.client_name.title"),
			},
			{
				id: "CLIENT_ID",
				name: t("modules.transaction_report.field.client_id.title"),
			},
			{
				id: "CATEGORY",
				name: t("common.word.category"),
			},
			{
				id: "ORIGINAL_NAME",
				name: "Nazwa na fakturze",
			},
			{
				id: "APP_NAME",
				name: "Aplikacja",
			},
			{
				id: "PLAN",
				name: "Plan",
			},
			{
				id: "DATE",
				name: "Data",
			},
			{
				id: "DATE_YEAR",
				name: "Data - rok",
			},
			{
				id: "DATE_MONTH",
				name: "Data - miesiąc",
			},
			{
				id: "SUBSCRIPTION_ID",
				name: "Subskrypcja",
			},
			{
				id: "ENTITY_ID",
				name: "Entity ID",
			},
			{
				id: "ENTITY_TYPE",
				name: "Entity type",
			},
			{
				id: "INVOICE_NUMBER",
				name: "Faktura",
			},
			{
				id: "COMPANY_PARTNER",
				name: "Partner",
			},
			{
				id: "COMPANY_FIRST_PAID_DATE",
				name: "Pierwsza płatność",
			},
			{
				id: "COMPANY_FIRST_PAID_DATE_YEAR",
				name: "Pierwsza płatność - rok",
			},
			{
				id: "COMPANY_FIRST_PAID_DATE_MONTH",
				name: "Pierwsza płatność - miesiąc",
			},
			{
				id: "COMPANY_CUSTOMER_TYPE",
				name: "Typ klienta",
			},
			{
				id: "COMPANY_GOPOS_VERSION",
				name: "Wersja GoPOS",
			},
			{
				id: "+SEASONAL.COMPANY",
				name: "Biznes sezonowy",
			},
			{
				id: "PARTNER_TYPE",
				name: "Typ partnera",
			},
			{
				id: "COMPANY_CANCELLATION_REASON",
				name: t("common.word.cancellation_reason"),
			},
			{
				id: "COMPANY_CANCELLATION_REASON_DETAILS",
				name: t("modules.transaction_report.field.cancellation_reason_details.title"),
			},
			{
				id: "SLUG",
				name: "SLUG",
			},
			{
				id: "STATUS",
				name: "Status",
			},
			{
				id: "PRODUCT_PARTNER",
				name: "Produkt - partner",
			},
			{
				id: "PRODUCT_PARTNER_TYPE",
				name: "Produkt - typ partnera",
			},
			{
				id: "PERIOD_MONTHS",
				name: "Ilość miesięcy (cykli)",
			},
		],
		showPercent: false,
		groupsInfo: {
			ORGANIZATION_NAME: (groupByValue: Record<string, any>) => {
				const link = `/admin/organizations?search=${groupByValue.name}`;
				return (
					<Link to={link} rel="noreferrer" target="_blank">
						{groupByValue.name}
					</Link>
				);
			},
			CLIENT_ID: (groupByValue: Record<string, any>) => {
				const link = `/admin/organizations?search=${groupByValue.name}`;
				return (
					<Link to={link} rel="noreferrer" target="_blank">
						{groupByValue.name}
					</Link>
				);
			},
		},
		columns: [
			{
				id: "quantity",
				name: "Ilość",
			},
			{
				id: "start_quantity_customer",
				name: "Customer Start Q",
			},
			{
				id: "reactivation_quantity_customer",
				name: "Customer Reactivation Q",
			},
			{
				id: "new_quantity_customer",
				name: "Customer New Q",
			},
			{
				id: "churn_quantity_customer",
				name: "Customer Churn Q",
			},
			{
				id: "quantity_customer",
				name: "Customer Q",
			},
			{
				id: "start_total_price",
				name: "Start $",
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "upgrade_total_price",
				name: "Upgrade $",
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "downgrade_total_price",
				name: "Downgrade $",
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "new_total_price",
				name: "new $",
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "reactivation_total_price",
				name: "reactivation $",
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "churn_total_price",
				name: "Churn $",
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "total_price",
				name: t("common.word.total_price"),
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "upselling_total_price",
				name: "upselling $",
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "sub_total_price",
				name: t("modules.transaction.field.sub_total_price.title"),
				render: (item: MoneyApi) => `${FormatMoney(item)}`,
			},
			{
				id: "start_quantity",
				name: "Start Q",
			},
			{
				id: "upgrade_quantity",
				name: "Upgrade Q",
			},
			{
				id: "downgrade_quantity",
				name: "Downgrade Q",
			},
			{
				id: "new_quantity",
				name: "new Q",
			},
			{
				id: "reactivation_quantity",
				name: "reactivation Q",
			},
			{
				id: "churn_quantity",
				name: "Churn Q",
			},
			{
				id: "solution_quantity",
				name: "Ilość Q",
			},
			{
				id: "upselling_quantity",
				name: "Upselling Q",
			},

			{
				id: "start_points",
				name: "Start PTS",
			},
			{
				id: "upgrade_points",
				name: "Upgrade PTS",
			},
			{
				id: "downgrade_points",
				name: "Downgrade PTS",
			},
			{
				id: "new_points",
				name: "new PTS",
			},
			{
				id: "reactivation_points",
				name: "reactivation PTS",
			},
			{
				id: "churn_points",
				name: "Churn PTS",
			},
			{
				id: "points",
				name: "Ilość PTS",
			},
			{
				id: "upselling_points",
				name: "Upselling PTS",
			},
		],
		selectedGroups: ["DATE"],
		filters: [
			{
				id: "date",
				name: "Data",
				type: "date",
			},
			{
				id: "transaction",
				name: t("modules.transaction.field.transaction.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListTransactionsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "sub_total_price",
				name: t("modules.transaction.field.sub_total_price.title"),
				type: "number",
			},
			{
				id: "total_price",
				name: t("common.word.total_price"),
				type: "number",
			},
			{
				id: "transaction_source",
				name: "Źródło danych",
				type: "list",
				options: {
					CHARGEBEE: t("enums.transaction_source.type.CHARGEBEE"),
					MANUAL: t("enums.transaction_source.type.MANUAL"),
				},
			},
			parseFiltersCF(
				{
					id: "company_source",
					name: "Źródło (Firma)",
				},
				"source"
			),
			{
				id: "client_tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "client_name",
				name: t("modules.transaction.field.client_name.title"),
				type: "text",
			},
			{
				id: "client_id",
				name: t("modules.transaction.field.client_id.title"),
				type: "text",
			},
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "list",
				options: productNames.reduce((map, type) => {
					map[type] = type;
					return map;
				}, {}),
			},
			{
				id: "category",
				name: t("common.word.category"),
				type: "text",
			},
			{
				id: "organization",
				name: t("lib:common.word.location"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminOrganizationsListSearchSelect(search, params, undefined, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "paid_at",
				name: t("modules.transaction.field.paid_at.title"),
				type: "date",
			},
			{
				id: "transaction_at",
				name: "Data transakcji",
				type: "date",
			},
			{
				id: "sub_total_price_aggregate",
				name: t("modules.transaction.field.sub_total_price_aggregate.title"),
				type: "number",
			},
			{
				id: "total_price_aggregate",
				name: t("modules.transaction.field.total_price_aggregate.title"),
				type: "number",
			},
			{
				id: "original_name",
				name: "Nazwa na fakturze",
				type: "text",
			},
			{
				id: "app_name",
				name: "Aplikacja",
				type: "text",
			},
			{
				id: "plan",
				name: "Plan",
				type: "text",
			},
			{
				id: "subscription_id",
				name: "Subskrypcja",
				type: "text",
			},
			{
				id: "entity_id",
				name: "Entity ID",
				type: "text",
			},
			{
				id: "entity_type",
				name: "Entity Type",
				type: "text",
			},
			{
				id: "invoice_number",
				name: "Faktura",
				type: "text",
				extraOptions: ["e"],
			},
			{
				id: "company_partner",
				name: "Partners",
				type: "text",
			},
			parseFiltersCF(
				{
					id: "+customer_type.COMPANY",
					name: "Typ klienta",
				},
				"customer_type"
			),
			{
				id: "company_first_paid_date",
				name: "Pierwsza płatność",
				type: "date",
				extraOptions: ["a", "u"],
			},
			parseFiltersCF({
				id: "company_gopos_version",
				name: "Wersja GoPOS",
			}),
			{
				id: "partner_type",
				name: `Typ partnera`,
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "product_partner_type",
				name: `Product - Typ partnera`,
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "organization_partner",
				name: "Partner",
				type: "search_select",
				extraOptions: ["ne"],
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminPartnersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "transaction_type_quantity",
				name: `Typ transakcji Q`,
				type: "list",
				options: transactionTypes.reduce((map, type) => {
					map[type] = type;
					return map;
				}, {}),
			},
			{
				id: "status",
				name: `Status`,
				type: "list",
				options: transactionStatuses.reduce((map, type) => {
					map[type] = type;
					return map;
				}, {}),
			},
			{
				id: "transaction_type_price",
				name: `Typ transakcji $`,
				type: "list",
				options: transactionTypes.reduce((map, type) => {
					map[type] = type;
					return map;
				}, {}),
			},
			{
				id: "upselling",
				name: "Zmiane cen",
				type: "boolean",
			},
			parseFiltersCF({
				id: "cancellation_reason",
				name: t("common.word.cancellation_reason"),
			}),
			{
				id: "cancellation_reason_details",
				name: t("modules.transaction.field.cancellation_reason_details.title"),
				type: "text",
			},
			{
				id: "upgrade_quantity",
				name: "Upgrade Q",
				type: "number",
			},
			{
				id: "downgrade_quantity",
				name: "Downgrade Q",
				type: "number",
			},
			{
				id: "new_quantity",
				name: "New Q",
				type: "number",
			},
			{
				id: "reactivation_quantity",
				name: "Reactivation Q",
				type: "number",
			},
			{
				id: "churn_quantity",
				name: "Churn Q",
				type: "number",
			},
			{
				id: "changed_quantity",
				name: "Changed Q",
				type: "number",
			},

			{
				id: "upgrade_price",
				name: "Upgrade $",
				type: "number",
			},
			{
				id: "downgrade_price",
				name: "Downgrade $",
				type: "number",
			},
			{
				id: "new_price",
				name: "New $",
				type: "number",
			},
			{
				id: "reactivation_price",
				name: "Reactivation $",
				type: "number",
			},
			{
				id: "churn_price",
				name: "Churn $",
				type: "number",
			},
			{
				id: "changed_price",
				name: "Changed $",
				type: "number",
			},
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		segments: [
			{
				id: "active",
				name: "Aktywne",
				slug: "active",
				filters: [
					{
						filterId: "status",
						value: `PAYMENT_DUE${FILTER_VALUE_SEPARATOR}PAID`,
						condition: "e",
					},
					{
						filterId: "date",
						value: "this_month",
						condition: "btd",
					},
				],
			},
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: [
					"start_quantity_customer",
					"new_quantity_customer",
					"reactivation_quantity_customer",
					"churn_quantity_customer",
					"quantity_customer",
					"start_total_price",
					"upgrade_total_price",
					"downgrade_total_price",
					"new_total_price",
					"reactivation_total_price",
					"churn_total_price",
					"total_price",
				],
				filters: [],
				groups: [],
			},
		],
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				adminApi.getTransactionsReport(reportParamsConverter(params), { cancelToken: sourceToken?.token }),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.transaction_report.header.title"),
			filename: t("modules.transaction_report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["ORGANIZATION", "PARTNER"], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return (
		<>
			<ReportData config={config} />
			{showSynchronizeReportModal && (
				<SynchronizeTransactionsReportModal
					isShown={showSynchronizeReportModal}
					onSuccessHandle={onRefresh}
					onHide={() => setShowSynchronizeReportModal(false)}
				/>
			)}
		</>
	);
};

const AdminTransactionsReportPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const [customFields, setCustomFields] = useState<CustomFieldTemplateApi[]>([]);
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const [showSynchronizeReportModal, setShowSynchronizeReportModal] = useState<boolean>(false);
	const { addFlash } = useFlash();

	const customFieldResourceTypes = [
		{
			type: "ORGANIZATION",
			name: t(`enums.custom_fields.resources.ORGANIZATION`),
		},
		{
			type: "PARTNER",
			name: t(`enums.custom_fields.resources.PARTNER`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const buttons: ButtonProps[] = [
		{
			title: t("modules.transaction_report.action.synchronize.title"),
			action: () => setShowSynchronizeReportModal(true),
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.transaction_report.action.synchronize.title"),
			action: () => setShowSynchronizeReportModal(true),
		},
	];
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleInit();
	}, []);

	const fetchCustomFields = async () => {
		try {
			const f = mapToBase("", {
				"slug|e": "keeper_type¥source¥cancellation_reason¥customer_type¥gopos_version,seasonal",
			});
			const res = await adminApi.getCustomFields({ f, include: "options" });
			setCustomFields(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleInit = async () => {
		handleChangeTabTitle(
			`${t("modules.transaction_report.header.title")} | ${t("lib:go_component.navigation.admin")}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
		await fetchCustomFields();
	};

	return (
		<div className={"content"}>
			{!isMobile && <Header title={t("modules.transaction_report.header.title")} buttons={buttons} />}
			<MobileActions actions={mobileActions} />
			<Suspense fallback={<LoadingContainer />}>
				<Report
					resource={resource}
					customFields={customFields}
					showSynchronizeReportModal={showSynchronizeReportModal}
					setShowSynchronizeReportModal={setShowSynchronizeReportModal}
				/>
			</Suspense>
		</div>
	);
};

export default AdminTransactionsReportPage;
